/**
 * Admin permission keys and route mapping.
 * If an admin doc has no `permissions` or includes 'full', they have full access.
 * Otherwise access is allowed only when the resource's permission is in the array.
 */

export const PERMISSION_KEYS = [
  'eventManifest',
  'scores',
  'tshirts',
  'teams',
  'events',
  'freshmeetHq',
  'tickets',
  'printables',
  'memoryBook',
  'awardsCeremony',
  'sponsors',
  'furyLab',
  'recurrencies',
  'backups',
  'users',
  'templates',
  'export',
  'testData',
  'freshmeetSite',
  'volunteers',
  'videoJudge',
];

/** Human-readable labels for permission keys (for User Management UI). */
export const PERMISSION_LABELS = {
  eventManifest: 'Event Day Manifest',
  scores: 'Scores (Sky, Bonus, Import)',
  tshirts: 'T-Shirts',
  teams: 'Teams (Flyers, Pairing, Load Order)',
  events: 'Events',
  freshmeetHq: 'Main Clue (Fresh Meet HQ)',
  tickets: 'Tickets',
  printables: 'Printables',
  memoryBook: 'Memory Book',
  awardsCeremony: 'Awards Ceremony',
  sponsors: 'Sponsors',
  furyLab: 'Fury Lab',
  recurrencies: 'Recurrencies',
  backups: 'Backups',
  users: 'Admins (view)',
  templates: 'Email templates',
  export: 'Team export',
  testData: 'Test Data',
  freshmeetSite: 'Fresh Meet site (page CMS)',
  volunteers: 'Volunteer shifts',
  videoJudge: 'Video Judge (sponsor thank-you videos)',
};

/**
 * Admin path (first segment, without /admin) -> required permission.
 * Use a string for one key, or an array if any listed permission grants access (for backward compatibility).
 * Index/dashboard uses null. Omitted paths are unrestricted for legacy routes.
 */
export const ROUTE_PERMISSIONS = {
  '': null, // dashboard - all admins can see
  events: 'events',
  tickets: 'tickets',
  tshirts: 'tshirts',
  printables: 'printables',
  // Memory Book: dedicated role, or legacy Printables access
  'event-memory-book': ['printables', 'memoryBook'],
  'photo-capture': ['printables', 'memoryBook'],
  'team-id-cards': ['printables', 'memoryBook', 'teams'],
  sponsors: 'sponsors',
  'fury-lab': 'furyLab',
  recurrencies: 'recurrencies',
  'flyer-management': 'teams',
  teams: 'teams',
  'team-pairing': 'teams',
  'load-order': 'teams',
  'event-manifest': 'eventManifest',
  'bonus-puzzle-management': 'scores',
  'jump-it-stations': 'scores',
  skyscore: 'scores',
  'bonus-scores': 'scores',
  'score-import': 'scores',
  // Main Clue workspace (HQ dashboard + sponsor-run questions tabs): event/HQ staff or sponsor-role editors
  'freshmeet-hq': ['events', 'freshmeetHq', 'sponsors'],
  // Legacy route + user-management label: sponsor-run tools only (same page as freshmeet-hq)
  'main-clue-sponsor-run': 'sponsors',
  // Awards: dedicated role, or legacy Printables access
  'awards-ceremony': ['printables', 'awardsCeremony'],
  live: 'events',
  'team-export': 'export',
  backups: 'backups',
  'test-data': 'testData',
  users: 'users',
  templates: 'templates',
  'freshmeet-site': 'freshmeetSite',
  volunteers: 'volunteers',
  'video-judge-queue': 'videoJudge',
};

/**
 * Returns whether the admin has the given permission.
 * @param {string[]|undefined} permissions - admin doc permissions array
 * @param {string} permission - required permission key
 * @returns {boolean}
 */
export function hasPermission(permissions, permission) {
  if (!permission) return true;
  if (!permissions || permissions.length === 0) return true;
  if (permissions.includes('full')) return true;
  return permissions.includes(permission);
}

/**
 * Returns the set of permissions the admin has (full = all keys).
 * @param {string[]|undefined} permissions - admin doc permissions array
 * @returns {Set<string>}
 */
export function allowedPermissions(permissions) {
  if (!permissions || permissions.length === 0 || permissions.includes('full')) {
    return new Set(PERMISSION_KEYS);
  }
  return new Set(permissions);
}

/**
 * Returns whether the admin can access the given admin path.
 * @param {string[]|undefined} permissions - admin doc permissions array
 * @param {string} path - admin path e.g. 'event-manifest' or 'events'
 * @returns {boolean}
 */
export function canAccessRoute(permissions, path) {
  const required = ROUTE_PERMISSIONS[path];
  if (required === undefined) return true;
  if (required === null) return true;
  if (Array.isArray(required)) {
    return required.some((p) => hasPermission(permissions, p));
  }
  return hasPermission(permissions, required);
}
